import { useState } from "react";
import { useCallback } from "react";
import { useEffect } from "react";
import { useMemo } from "react";
import { IProduct } from "../types";
import { useSnackbar } from "notistack";
import usePaymentQueries from "./usePaymentQueries";

export function usePaymentProvider() {
  const { enqueueSnackbar } = useSnackbar();
  const { fetchProducts } = usePaymentQueries();

  const [cart, _setCart] = useState<IProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState<boolean>(true);
  const [products, setProducts] = useState<IProduct[]>([]);

  const packages = useMemo(
    () => products.filter((item) => item.type === 1),
    [products],
  );
  const additional_services = useMemo(
    () => products.filter((item) => item.type === 2),
    [products],
  );

  useEffect(() => {
    let savedCart: string | null = localStorage.getItem("cart");
    if (savedCart && products.length) {
      let savedCartParsed: IProduct[] = JSON.parse(savedCart);
      if (savedCartParsed && Array.isArray(savedCartParsed)) {
        savedCartParsed = savedCartParsed.filter((item) => item.type === 1); //TODO remove this line when will add the possibility to buy many products

        let cartIds = savedCartParsed.map((item) => item && item.id);
        let cartProducts = products.filter((product) =>
          cartIds.includes(product.id),
        );
        let cartPackages = cartProducts.filter((item) => item.type === 1);
        let cartExtras = cartProducts.filter((item) => item.type !== 1);
        if (cartPackages.length) {
          cartProducts = [cartPackages[0], ...cartExtras];
        }
        setCart(cartProducts);
      }
    }
  }, [products]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCart = useCallback(
    async (items: IProduct[]) => {
      _setCart(items);
      localStorage.setItem("cart", JSON.stringify(items));
    },
    [_setCart],
  );

  const addToCart = useCallback(
    (id) => {
      let item = products.find((products_item) => products_item.id === id);

      if (item && !cart.map((cart_item) => cart_item.id).includes(item.id)) {
        let newCart =
          item.type === 1
            ? [item, ...cart.filter((cart_item) => cart_item.type !== 1)]
            : [item, ...cart];
        setCart(newCart);
      }
    },
    [products, cart],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const addRedirectUrl = useCallback((url) => {
    localStorage.setItem("checkoutRedirectUrl", url);
  }, []);

  const getRedirectUrl = useCallback(() => {
    return localStorage.getItem("checkoutRedirectUrl");
  }, []);

  const removeFromCart = useCallback(
    (id: number) => {
      let item = products.find((it) => it.id === id);
      if (item) {
        let newCart = [...cart.filter((item) => item.id !== id)];
        setCart(newCart);
      }
    },
    [cart],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const cleanCart = useCallback(() => {
    setCart([]);
  }, [setCart]);

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const { data } = await fetchProducts();
      setProducts(data);
      setLoadingProducts(false);
    } catch (e) {
      enqueueSnackbar("Couldn't load packages", { variant: "error" });
      setLoadingProducts(false);
    }
  }, [fetchProducts]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    loadProducts,
    loadingProducts,
    data: {
      cart,
      products,
      packages,
      additional_services,
    },
    actions: {
      addToCart,
      cleanCart,
      removeFromCart,
      addRedirectUrl,
      getRedirectUrl,
    },
  };
}
